<script setup lang="ts">
import { ArrowLeft, Bookmark, CalendarPlus, ExternalLink, MapPin, Share2 } from '../components/wx-icons'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ActivityCard from '../components/ActivityCard.vue'
import InventoryMatrix from '../components/InventoryMatrix.vue'
import StatePanel from '../components/StatePanel.vue'
import VenueHero from '../components/VenueHero.vue'
import { api, listOf } from '../services/api'
import { authState } from '../services/auth'
import { distance } from '../services/format'

const route = useRoute()
const router = useRouter()
const venue = ref<any>(null)
const activities = ref<any[]>([])
const loading = ref(true)
const error = ref('')
const saving = ref(false)
const activeTab = ref<'activities' | 'booking'>('booking')
const galleryOpen = ref(false)
const galleryIndex = ref(0)
const feedbackOpen = ref(false)
const feedbackText = ref('')
const feedbackSaving = ref(false)
const localDistanceText = ref('')
const venueTagLabels: Record<string, string> = {
  hard: '硬地', clay: '红土', grass: '草地', synthetic: '人工草地',
  outdoor: '室外', shelter: '风雨棚', indoor: '室内', covered: '气膜馆',
  machine_training: '发球机学练馆',
}
const id = computed(() => String(route.params.id || ''))
const tags = computed(() => {
  const value = venue.value
  if (!value) return []
  return [...(value.surfaceTypes || []).map((item:any)=>({value:item,kind:'surface'})), ...(value.natures || []).map((item:any)=>({value:item,kind:'nature'})), ...(value.courtTags || []).map((item:any)=>({value:item,kind:'surface'})), ...(value.facilities || []).map((item:any)=>({value:item,kind:'facility'}))]
    .map((item: any) => ({ label:item.value?.label || item.value?.name || venueTagLabels[String(item.value)] || item.value, kind:item.kind }))
    .filter(Boolean)
    .slice(0, 8)
})
const businessHours = computed(() => {
  const ranges = (venue.value?.courts || []).flatMap((court:any)=>court?.pricings || []).map((pricing:any)=>({start:String(pricing?.startTime||'').slice(0,5),end:String(pricing?.endTime||'').slice(0,5)})).filter((item:any)=>item.start&&item.end)
  if (!ranges.length) return '待收录'
  return `${ranges.map((item:any)=>item.start).sort()[0]}-${ranges.map((item:any)=>item.end).sort().at(-1)}`
})
const distanceText = computed(() => distance(route.query.distanceMeters) || localDistanceText.value)
const currentActivities = computed(() => activities.value.filter(item => activityEnd(item) >= Date.now() && Number(item.status) !== 4))
const pastActivities = computed(() => activities.value.filter(item => activityEnd(item) < Date.now() || Number(item.status) === 4))

function activityEnd(item:any){return new Date(`${item?.activityDate||item?.date||''}T${item?.endTime||'23:59'}:00`).getTime()||Number.POSITIVE_INFINITY}
function fillLocalDistance(){if(route.query.distanceMeters!=null||!navigator.geolocation)return;navigator.geolocation.getCurrentPosition(position=>{const lat=Number(venue.value?.latitude),lng=Number(venue.value?.longitude);if(!Number.isFinite(lat)||!Number.isFinite(lng))return;const rad=(value:number)=>value*Math.PI/180;const dLat=rad(lat-position.coords.latitude),dLng=rad(lng-position.coords.longitude);const a=Math.sin(dLat/2)**2+Math.cos(rad(position.coords.latitude))*Math.cos(rad(lat))*Math.sin(dLng/2)**2;localDistanceText.value=distance(6371000*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a)))},undefined,{enableHighAccuracy:false,timeout:5000,maximumAge:300000})}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [detail, related] = await Promise.all([
      api<any>(`/venues/${encodeURIComponent(id.value)}`),
      api<any>(`/activity/search?venueId=${encodeURIComponent(id.value)}&page=1&pageSize=6`).catch(() => []),
    ])
    venue.value = detail
    fillLocalDistance()
    activities.value = listOf(related)
  } catch (reason: any) {
    error.value = reason?.message || '场馆加载失败'
  } finally {
    loading.value = false
  }
}

async function toggleFavorite() {
  if (!authState.value) return router.push({ name: 'login', query: { redirect: route.fullPath } })
  if (saving.value) return
  saving.value = true
  const next = !venue.value?.isFavorite
  try {
    await api(`/venues/${encodeURIComponent(id.value)}/favorite`, { method: next ? 'POST' : 'DELETE' })
    venue.value = { ...venue.value, isFavorite: next }
  } finally {
    saving.value = false
  }
}

function copyShareLink() {
  void window.navigator.clipboard?.writeText(window.location.href)
}

function openLocation(){const lat=Number(venue.value?.latitude),lng=Number(venue.value?.longitude);if(!Number.isFinite(lat)||!Number.isFinite(lng))return;window.open(`https://apis.map.qq.com/uri/v1/marker?marker=coord:${lat},${lng};title:${encodeURIComponent(venue.value?.name||'场馆')};addr:${encodeURIComponent(venue.value?.address||'')}`,'_blank','noopener,noreferrer')}
async function submitFeedback(){if(!authState.value)return router.push({name:'login',query:{redirect:route.fullPath}});if(!feedbackText.value.trim()||feedbackSaving.value)return;feedbackSaving.value=true;try{await api('/venues/feedback',{method:'POST',body:JSON.stringify({venueId:id.value,venueName:venue.value?.name||'',venueAddress:venue.value?.address||'',latitude:Number(venue.value?.latitude)||undefined,longitude:Number(venue.value?.longitude)||undefined,bookingDetail:feedbackText.value.trim(),submitterType:'web-user'})});feedbackOpen.value=false;feedbackText.value='';window.alert('感谢反馈，我们会尽快跟进')}finally{feedbackSaving.value=false}}

onMounted(load)
</script>

<template>
  <div class="shell detail-page venue-detail-page">
    <div class="venue-page-tools">
      <button class="back-link" type="button" @click="router.back()"><ArrowLeft :size="18" />返回找球</button>
      <button class="button button-secondary" type="button" @click="copyShareLink"><Share2 :size="17" />分享</button>
    </div>
    <StatePanel v-if="loading" mode="loading" />
    <StatePanel v-else-if="error" mode="error" :description="error" @retry="load" />
    <template v-else-if="venue">
      <section class="detail-hero venue-detail-hero">
        <button class="venue-gallery-trigger" type="button" @click="galleryOpen=true"><VenueHero :name="venue.displayName || venue.name" :image="venue.images?.[0]" :images="venue.images" :tone-key="id" :province="venue.province" :city="venue.city" :district="venue.district" /></button>
        <div class="detail-hero-copy">
          <div class="venue-title-row">
            <h1>{{ venue.displayName || venue.name }}<small v-if="venue.transitionFormerNameDisplay">原{{ venue.transitionFormerNameDisplay }}</small></h1>
            <button class="icon-button" :class="{ active: venue.isFavorite }" type="button" title="收藏" @click="toggleFavorite"><Bookmark :size="20" /></button>
          </div>
          <div class="tag-row"><span v-for="tag in tags" :key="tag.label" class="chip" :class="`venue-tag-${tag.kind}`">{{ tag.label }}</span></div>
          <div class="venue-fact-row"><MapPin :size="18" /><span>{{ venue.address || '暂无地址信息' }}<b v-if="distanceText">距离 {{ distanceText }}</b></span><button type="button" @click="openLocation">导航 ↗</button></div>
          <div class="venue-fact-row"><span class="fact-symbol">◷</span><span>营业时间</span><strong>{{ businessHours }}</strong></div>
          <button class="venue-report-link" type="button" @click="feedbackOpen=true">场馆信息有误？告诉我们</button>
          <div class="detail-actions">
            <button class="button button-primary" type="button" @click="activeTab = 'booking'"><ExternalLink :size="18" />查看场地预约</button>
            <RouterLink class="button button-secondary" :to="{ name: 'publish-activity', query: { venueId: id, venueName: venue.name } }"><CalendarPlus :size="18" />在这里组局</RouterLink>
          </div>
        </div>
      </section>
      <nav class="venue-tabs" aria-label="场馆内容">
        <button :class="{ active: activeTab === 'activities' }" type="button" @click="activeTab = 'activities'">热门活动</button>
        <button :class="{ active: activeTab === 'booking' }" type="button" @click="activeTab = 'booking'">场地预约</button>
      </nav>
      <InventoryMatrix v-if="activeTab === 'booking'" :venue-id="id" :venue-name="venue.displayName || venue.name" :booking-notice="venue.bookingNotice" />
      <section v-else class="venue-activity-panel">
        <div class="section-heading compact"><div><h2>热门活动</h2><p>当前有 {{ activities.length }} 场公开球局</p></div><RouterLink :to="{ name: 'find', query: { tab: 'activities', venueId: id } }">查看更多</RouterLink></div>
        <div v-if="currentActivities.length" class="activity-list"><ActivityCard v-for="activity in currentActivities" :key="activity.id" :activity="activity" /></div>
        <StatePanel v-else mode="empty" title="这里还没有公开球局" description="发起一场活动，邀请球友来这里打球。" />
        <template v-if="pastActivities.length"><p class="venue-past-divider">以下是曾在该场馆开展的球局</p><div class="activity-list"><ActivityCard v-for="activity in pastActivities" :key="activity.id" :activity="activity" /></div></template>
      </section>
      <section v-if="venue.description" class="content-card venue-description"><h2>场馆介绍</h2><p>{{ venue.description }}</p></section>
      <div v-if="galleryOpen" class="venue-modal-backdrop gallery-backdrop" @click.self="galleryOpen=false"><section class="venue-gallery-modal"><button type="button" @click="galleryOpen=false">×</button><template v-if="venue.images?.length"><img :src="venue.images[galleryIndex]" :alt="`${venue.name}实拍图`"><div><button type="button" @click="galleryIndex=(galleryIndex-1+venue.images.length)%venue.images.length">‹</button><span>{{ galleryIndex+1 }} / {{ venue.images.length }}</span><button type="button" @click="galleryIndex=(galleryIndex+1)%venue.images.length">›</button></div></template><p v-else>实拍图待场馆方上传</p></section></div>
      <div v-if="feedbackOpen" class="venue-modal-backdrop" @click.self="feedbackOpen=false"><section class="venue-modal"><h3>场馆信息反馈</h3><p>请说明需要更正的信息，我们核实后更新。</p><textarea v-model="feedbackText" rows="5" placeholder="例如：营业时间、订场方式或地址有误"></textarea><div class="modal-actions"><button class="button button-secondary" type="button" @click="feedbackOpen=false">取消</button><button class="button button-primary" type="button" :disabled="!feedbackText.trim()||feedbackSaving" @click="submitFeedback">{{ feedbackSaving?'提交中…':'提交反馈' }}</button></div></section></div>
    </template>
  </div>
</template>
